import React, { useCallback, useEffect, useMemo, useState } from "react";
import TableList from "../../generic/elements/tablelist/TableList";
import SearchInput from "../../generic/elements/search/SearchInput";
import "./patientlist.css";
import TableColumns from "./TableColumns";
import IntentType from "../../../intentexecutor/utils/IntentType";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  hasMorePatientDataState,
  patientListPageState,
  patientListSearchQuery,
  patientListTableDataState,
  patientListTotalCount,
} from "../../../components/recoil/patientListRecoil";
import MemoryType from "../../../service/memory/enum/MemoryType";
import { useReadMemory } from "../../generic/hooks/useReadMemory";
import { executeIntent } from "../../utils/IntentExecutor";
import ConfigurationConstants from "../../utils/configurationconstants";
import { currentConfiguration } from "../../../service/memory/recoil/configurationstore";
import PrimaryButton from "../../../components/generic/buttons/PrimaryButton";
import { Col, notification, Row } from "antd";
import BackButton from "../../generic/elements/backbutton/BackButton";
import CustomScrollbar from "../../../components/generic/scrollbar/CustomScrollbar";
import { mapIntentToRequest } from "../../../service/network/RequestMapper";
import { getContext } from "../../../intentexecutor/contextcreator/ContextCreator";
import { getApiUrl } from "../../../service/network/UrlMapper";
import { performPostRequest } from "../../../service/network/NetworkService";
import AddEditPatientModal, {
  patientDataUpdateResponse,
} from "../patient/AddEditPatientModal";
import { teamList } from "../../../components/recoil/teamsRecoil";
import { useObservable } from "../../generic/hooks/useObservable";
import { useGetOrgIdentifier } from "../../generic/hooks/useGetOrgIdentifier";
import { EventType } from "../../../service/network/utils/EventType";
import SearchAndSelect from "../../../components/generic/search/SearchAndSelect";

const PatientListComponent = () => {
  const [tableData, setTableData] = useRecoilState(patientListTableDataState);
  const [hasMoreData, setHasMoreData] = useRecoilState(hasMorePatientDataState);
  const [page, setPage] = useRecoilState(patientListPageState);
  const [totalCount, setTotalCount] = useRecoilState(patientListTotalCount);
  const teams = useRecoilValue(teamList);
  const [searchQuery, setSearchQuery] = useRecoilState(patientListSearchQuery);
  const [tableLoading, setTableLoading] = useState(false);
  const { columns } = TableColumns();
  const token = useReadMemory(MemoryType.TOKEN);
  const userJson = useReadMemory(MemoryType.USER);
  const configuration = useRecoilValue(currentConfiguration);
  const secretKey =
    configuration[ConfigurationConstants.NIRAMAYA_ORGANIZATION_SECRETKEY]?.key;
  const [showSyncButton, setShowSyncButton] = useState(false);
  const [showAddPatientButton, setShowAddPatientButton] = useState(false);
  const [migrationPayload, setMigrationPayload] = useState({});
  const [syncButtonLoading, setSyncButtonLoading] = useState(false);
  const organizationIdentifier = useGetOrgIdentifier();
  const [options, setOptions] = useState([]);
  const [defaultPatientStatus, setDefaultPatientStatus] = useState(false);
  const [patientStatus, setPatientStatus] = useState([]);
  const [reload, setReload] = useState(false);
  const [openAddPatientModal, setOpenAddPatientModal] = useState(false);
  useObservable(patientDataUpdateResponse, setReload);

  const searchPayload = useMemo(
    () => ({
      page: 1,
      query: searchQuery,
      organizationIdentifier: userJson?.organizationIdentifier,
      secretKey: secretKey,
      filters: {
        patientStatus: patientStatus.map((status) => status.value),
      },
    }),
    [searchQuery, userJson?.organizationIdentifier, secretKey, patientStatus],
  );

  const context = useMemo(() => {
    const base = getContext();
    const fromWorkforce = Array.isArray(userJson?.teams)
      ? userJson.teams.map((item) => item?.id).filter((id) => id != null)
      : [];
    return {
      ...base,
      teamIds: teams?.length ? base.teamIds : fromWorkforce,
    };
  }, [teams?.length, userJson.teams]);

  const fetchPatientListData = useCallback(async () => {
    setTableLoading(true);
    const payload = {
      page: 1,
      organizationIdentifier: userJson?.organizationIdentifier,
      secretKey: secretKey,
      filters: {
        patientStatus: patientStatus.map((status) => status.value),
      },
    };
    const request = mapIntentToRequest(
      IntentType.GET_ALL_PATIENTS,
      payload,
      context,
    );
    const urlMapper = getApiUrl();
    const result = await performPostRequest(
      urlMapper[IntentType.GET_ALL_PATIENTS],
      JSON.stringify(request),
    );
    const responseData = await result.json();
    setTableData(
      Array.isArray(responseData?.patientList) ? responseData?.patientList : [],
    );
    setTotalCount(responseData?.totalCount || 0);
    setTableLoading(false);
    if (responseData?.patientList?.length < 100) {
      setHasMoreData(false);
    } else {
      setPage(2);
      setHasMoreData(true);
    }
  }, [
    userJson?.organizationIdentifier,
    secretKey,
    patientStatus,
    context,
    setTableData,
    setTotalCount,
    setHasMoreData,
    setPage,
  ]);

  const fetchMoreData = useCallback(async () => {
    const payload = {
      page: page,
      organizationIdentifier: userJson?.organizationIdentifier,
      secretKey: secretKey,
      filters: {
        patientStatus: patientStatus.map((status) => status.value),
      },
    };
    if (hasMoreData) {
      const request = mapIntentToRequest(
        IntentType.GET_ALL_PATIENTS,
        payload,
        context,
      );
      const urlMapper = getApiUrl();
      const result = await performPostRequest(
        urlMapper[IntentType.GET_ALL_PATIENTS],
        JSON.stringify(request),
      );
      const responseData = await result.json();
      if (responseData?.patientList?.length > 0) {
        setTableData((prevData) => [...prevData, ...responseData?.patientList]);
        setTotalCount(responseData?.totalCount);
        setPage(page + 1);
        if (responseData?.patientList?.length === 100) {
          setHasMoreData(true);
        } else {
          setHasMoreData(false);
        }
      } else {
        setHasMoreData(false);
      }
    }
  }, [
    page,
    userJson?.organizationIdentifier,
    secretKey,
    patientStatus,
    hasMoreData,
    context,
    setTableData,
    setTotalCount,
    setPage,
    setHasMoreData,
  ]);

  const fetchSearchedData = useCallback(async () => {
    setTableLoading(true);
    try {
      const request = mapIntentToRequest(
        IntentType.SEARCH_PATIENT,
        searchPayload,
        context,
      );
      const urlMapper = getApiUrl();
      const result = await performPostRequest(
        urlMapper[IntentType.GET_ALL_PATIENTS],
        JSON.stringify(request),
      );

      const responseData = await result.json();
      setTableData(
        Array.isArray(responseData?.patientList)
          ? responseData?.patientList
          : [],
      );
      setTotalCount(responseData?.totalCount || 0);
      if (responseData?.patientList?.length < 100) {
        setHasMoreData(false);
      } else {
        setPage(2);
        setHasMoreData(true);
      }
    } finally {
      setTableLoading(false);
    }
  }, [
    searchPayload,
    context,
    setTableData,
    setTotalCount,
    setHasMoreData,
    setPage,
  ]);

  const handleAddPatient = () => {
    setOpenAddPatientModal(true);
  };

  const handleSyncData = async () => {
    setSyncButtonLoading(true);
    const payload = {
      organizationId: migrationPayload?.organizationId,
      hospiceId: migrationPayload?.hospiceId,
      decryptionKey: migrationPayload?.decryptionKey,
      token,
    };

    try {
      const response = await executeIntent(
        IntentType.MIGRATE_PDV_NEW_PATIENTS,
        payload,
      );
      if (response?.status === "OK") {
        notification.success({
          message: "Patients synced successfully",
          placement: "bottomRight",
        });
      } else {
        notification.error({
          message: "Failed to sync patients. Please try again",
          placement: "bottomRight",
        });
      }
      patientDataUpdateResponse.next({
        type: EventType.PATIENT_DATA_UPDATED,
      });
    } catch (err) {
      notification.error({
        message: "Failed to sync patients. Please try again",
        placement: "bottomRight",
      });
    } finally {
      setSyncButtonLoading(false);
    }
  };

  useEffect(() => {
    const getAdminIdentifiers = async () => {
      try {
        const payload = {
          intent: "get_configuration",
          payload: {
            configurationKey: [
              "niramaya.featureaccess.sync.patients",
              "niramaya.migrate.patients.payload",
            ],
          },
          context: {
            organizationIdentifier,
          },
        };
        const result = await performPostRequest(
          getApiUrl()[IntentType.GET_CONFIGURATION],
          JSON.stringify(payload),
        );
        const response = await result.json();
        const identifiers = JSON.parse(response[0].configurationValue || "[]");
        const migrationButtonPayload = JSON.parse(
          response[1].configurationValue || "{}",
        );
        const isAllowed = identifiers?.includes(userJson?.userIdentifier);
        setShowSyncButton(isAllowed);
        setMigrationPayload(migrationButtonPayload);
      } catch (err) {
        setShowSyncButton(false);
      }
    };

    getAdminIdentifiers();
  }, [organizationIdentifier, userJson?.userIdentifier]);

  useEffect(() => {
    if (searchQuery.length > 0) {
      fetchSearchedData();
    } else {
      fetchPatientListData();
    }
  }, [
    searchQuery,
    secretKey,
    teams,
    reload,
    patientStatus,
    fetchSearchedData,
    fetchPatientListData,
  ]);

  useEffect(() => {
    const getOptions = async () => {
      const payload = {
        configurationKey: [
          "niramaya.add.patient.options",
          "niramaya.default.patient.status",
        ],
      };

      const result = await executeIntent(
        IntentType.GET_CONFIGURATION,
        payload,
        { organizationIdentifier },
      );
      const options =
        result[0].configurationValue &&
        JSON.parse(result[0].configurationValue);
      setOptions(options?.patientStatus?.menuItems);

      const defaultStatus =
        result[1].configurationValue &&
        JSON.parse(result[1].configurationValue);
      setPatientStatus(defaultStatus);
      if (defaultStatus) {
        setDefaultPatientStatus(true);
      }
    };

    if (!defaultPatientStatus) {
      getOptions();
    }
  }, [defaultPatientStatus, organizationIdentifier]);

  useEffect(() => {
    const configurationAddPatient =
      configuration[
        ConfigurationConstants.FEATUREACCESS_PATIENTLIST_ADD_PATIENT
      ];
    setShowAddPatientButton(configurationAddPatient?.visibility);
  }, [configuration]);

  return (
    <>
      <section className="section-content">
        <div className="fixed-content">
          <div className="form-renderer-back-button workforce-list-header-row">
            <div className="workforce-list-header-title">
              <BackButton />
              <p>Patients</p>
            </div>
            <div className="workforce-list-total-count">
              <span className="workforce-list-total-count-label">
                Total Count:
              </span>
              <span className="workforce-list-total-count-value">
                {totalCount}
              </span>
            </div>
          </div>

          <div className="careplan-wrapper row" style={{ marginBlock: 10 }}>
            <div className="col-12 col-md-6 careplan-search-wrapper">
              <SearchInput
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                cssClass="searchFilterContainer"
                searchPlaceholder="Search patient..."
              />
            </div>

            {defaultPatientStatus && (
              <div className="col-12 col-md-6">
                <div className="careplan-select-wrapper">
                  <SearchAndSelect
                    mode="multiple"
                    selectedValues={patientStatus}
                    placeholder="Click here to select status"
                    setValue={setPatientStatus}
                    style={{
                      width: "100%",
                    }}
                    options={options}
                    selectOptionType="Simple"
                    noDebounceSearch={true}
                  />
                </div>
              </div>
            )}

            <Row style={{ gap: 4 }} justify="end">
              {showSyncButton && (
                <Col>
                  <PrimaryButton
                    buttonText="Sync Patients"
                    handleOnClick={handleSyncData}
                    loading={syncButtonLoading}
                  />
                </Col>
              )}
              {showAddPatientButton && (
                <Col>
                  <PrimaryButton
                    buttonText="Add New Patient"
                    handleOnClick={handleAddPatient}
                  />
                </Col>
              )}
            </Row>
          </div>
        </div>
        <div className="patient-table-list">
          <CustomScrollbar>
            {tableData.length !== undefined && (
              <TableList
                data={tableData}
                columns={columns}
                hasMoreData={hasMoreData}
                searchQuery={searchQuery}
                tableLoading={tableLoading}
                fetchMoreData={fetchMoreData}
              />
            )}
          </CustomScrollbar>
          {openAddPatientModal && (
            <AddEditPatientModal
              openAddPatientModal={openAddPatientModal}
              setOpenAddPatientModal={setOpenAddPatientModal}
            />
          )}
        </div>
      </section>
    </>
  );
};

export default PatientListComponent;
